import { Box, Tab, Tabs } from '@mui/material';
import ForbiddenTooltip from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import { PermissionTranslationKeys } from 'shared/domain/permissions/translationKeys';
import type { ChartFilter, ChartFiltersUnion } from 'shared/types/analytics';
import { isNonEmptyArray } from 'shared/utils/array';
import React, { ReactElement } from 'react';
import { FilterControlElement } from './components/FilterControlElement';
import { FilterTabPanel } from './components/FilterTabPanel';
import {
  emptyFiltersMessageSx,
  filtersRendererGridSx,
  filtersRendererPanelWrapperSx,
  getFiltersRendererRootSx,
  getFiltersRendererTabsSx,
  tabIndicatorPropsDesktop,
} from './styles';
import type { FiltersRendererPresentationalProps } from './types';

function tabWithFiltersProps(index: number): {
  id: string;
  'aria-controls': string;
} {
  return {
    id: `chart-filters-tab-${index}`,
    'aria-controls': `chart-filters-tabpanel-${index}`,
  };
}

type FilterGroupControlsProps = Pick<
  FiltersRendererPresentationalProps,
  'filtersDisabled' | 'availableFieldsSet' | 'formatEmptyLabel'
> &
  Readonly<{ controls: ChartFilter<unknown>[] }>;

/** The controls of one group of a chart's filters; those of issue fields hidden in the project are left out. */
function FilterGroupControls({
  controls,
  filtersDisabled,
  availableFieldsSet,
  formatEmptyLabel,
}: FilterGroupControlsProps): ReactElement {
  if (!isNonEmptyArray(controls)) {
    return (
      <Box component="p" sx={emptyFiltersMessageSx}>
        {formatEmptyLabel()}
      </Box>
    );
  }
  return (
    <>
      {controls
        .filter((control) => !control.fieldRelation || availableFieldsSet.has(control.fieldRelation))
        .map((control) => (
          <FilterControlElement
            key={control.labelId + control.type}
            type={control.type}
            filter={control}
            localSetter={control.setter}
            localValue={control.value}
            disabled={filtersDisabled}
          />
        ))}
    </>
  );
}

function controlsOf(filters: ChartFiltersUnion, group: keyof ChartFiltersUnion): ChartFilter<unknown>[] {
  return filters[group].filters;
}

/**
 * The filters of a chart in its details. Groups of filters are tabs; a chart
 * with one group (most of them: the data scope) shows its controls at once,
 * without a tab of its own (PT-6804).
 */
export function FiltersRendererPresentational({
  isMobile,
  filtersDisabled,
  filterTypes,
  filters,
  tabIndex,
  availableFieldsSet,
  onTabChange,
  formatFilterLabel,
  formatEmptyLabel,
}: FiltersRendererPresentationalProps): ReactElement {
  const controlsProps = { filtersDisabled, availableFieldsSet, formatEmptyLabel };
  const [onlyGroup, ...otherGroups] = filterTypes;
  const hasTabs = isNonEmptyArray(otherGroups);

  return (
    <Box sx={getFiltersRendererRootSx(isMobile)}>
      {hasTabs ? (
        <Tabs
          sx={getFiltersRendererTabsSx(isMobile)}
          variant="fullWidth"
          value={tabIndex}
          onChange={onTabChange}
          aria-label="filters-tab"
          orientation={isMobile ? 'horizontal' : 'vertical'}
          indicatorColor="primary"
          TabIndicatorProps={!isMobile ? tabIndicatorPropsDesktop : undefined}
        >
          {filterTypes.map((group, panelIndex) => (
            <Tab
              key={filters[group].labelId}
              className="tab"
              label={formatFilterLabel(filters[group].labelId)}
              {...tabWithFiltersProps(panelIndex)}
            />
          ))}
        </Tabs>
      ) : null}

      <ForbiddenTooltip visibleWhen={filtersDisabled} reason={PermissionTranslationKeys.OFFLINE_UNAVAILABLE}>
        <Box sx={filtersRendererPanelWrapperSx}>
          {hasTabs
            ? filterTypes.map((group, panelIndex) => (
                <FilterTabPanel key={group} value={tabIndex} index={panelIndex}>
                  <FilterGroupControls controls={controlsOf(filters, group)} {...controlsProps} />
                </FilterTabPanel>
              ))
            : onlyGroup && (
                <Box sx={filtersRendererGridSx}>
                  <FilterGroupControls controls={controlsOf(filters, onlyGroup)} {...controlsProps} />
                </Box>
              )}
        </Box>
      </ForbiddenTooltip>
    </Box>
  );
}

export const MemoFiltersRendererPresentational = React.memo(
  FiltersRendererPresentational
);
